<template>
  <div>
    <v-list subheader>
      <v-data-table
        dense
        :headers="tableHeaders"
        :items="value"
        :custom-sort="customSort"
        item-key="range"
        class="elevation-1"
      >
        <template v-slot:top>
          <v-toolbar
            flat
            color="white"
          >
            <v-toolbar-title>{{ label }}</v-toolbar-title>
            <v-divider
              class="mx-4"
              inset
              vertical
            />
            <v-spacer />
            <v-dialog
              v-model="dialog"
              max-width="60%"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :id="id + '-new-range-button'"
                  color="primary"
                  dark
                  class="mb-2"
                  v-bind="attrs"
                  :class="{'new-btn': disableNewBtn()}"
                  v-on="on"
                  @click="getInclusionTypes"
                >
                  New
                </v-btn>
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ label }} - New</span>
                </v-card-title>

                <v-card-text>
                  <v-container>
                    <v-row>
                      <v-col cols="6">
                        <v-form
                          ref="form"
                          v-model="rangeFormValid"
                          lazy-validation
                        >
                          <v-row>
                            <v-col>
                              <v-select
                                :id="id + '-inclusionType'"
                                v-model="inclusionType"
                                :items="inclusionTypes"
                                :error="error"
                                item-text="description"
                                :label="labelTxt + ' Type'"
                                :rules="[inputRules.required]"
                                filled
                                dense
                                @change="handleInputFields"
                              />
                            </v-col>
                          </v-row>
                          <template v-if="inclusionType!=null">
                            <v-row v-if="inclusionTypeCode=='RWDISSP'">
                              <v-col>
                                <v-autocomplete
                                  :id="id + '-from'"
                                  v-model="participant"
                                  :items="partAutocomplete.participants"
                                  :loading="partAutocomplete.loading"
                                  :search-input.sync="partAutocomplete.search"
                                  :rules="[inputRules.required]"
                                  :item-text="partAutocompleteItemText"
                                  label="Reward Issuing Participant"
                                  hide-no-data
                                  filled
                                  chips
                                  return-object
                                />
                              </v-col>
                            </v-row>
                            <v-row v-if="inclusionTypeCode=='ACCTID'">
                              <v-col>
                                <v-text-field
                                  :id="id + '-from'"
                                  v-model="from"
                                  :error="error"
                                  filled
                                  dense
                                  label="Value"
                                  :rules="[inputRules.required, inputRules.positiveNumber, inputRules.maxLength22]"
                                  @input="clearErrors"
                                />
                              </v-col>
                            </v-row>
                            <template v-if="inclusionTypeCode=='CRDRG'">
                              <v-row>
                                <v-col>
                                  <v-select
                                    :id="id + '-rangeType'"
                                    v-model="rangeType"
                                    :items="rangeTypes"
                                    :error="error"
                                    item-text="name"
                                    label="Range Type"
                                    :rules="[inputRules.required]"
                                    filled
                                    dense
                                  />
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col>
                                  <v-text-field
                                    :id="id + '-from'"
                                    v-model="from"
                                    :error="error"
                                    filled
                                    dense
                                    label="Range from"
                                    :rules="[inputRules.required,inputRules.alphanumeric, inputRules.maxLength22]"
                                    @input="clearErrors"
                                  />
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col>
                                  <v-text-field
                                    :id="id + '-to'"
                                    v-model="to"
                                    :error="error"
                                    filled
                                    dense
                                    label="Range to"
                                    :rules="[inputRules.required,inputRules.alphanumeric, inputRules.maxLength22]"
                                    @input="clearErrors"
                                  />
                                </v-col>
                              </v-row>
                            </template>
                            <v-row>
                              <v-col>
                                <v-text-field
                                  :id="id + '-description'"
                                  v-model="description"
                                  filled
                                  dense
                                  :rules="[v => (!v || v.length <= 100) || 'Max 100 characters']"
                                  counter="100"
                                  label="Description"
                                />
                              </v-col>
                            </v-row>
                          </template>
                          <v-row>
                            <v-col>
                              <div
                                v-if="error"
                                class="v-messages theme--light error--text"
                                role="alert"
                              >
                                <div class="v-messages__wrapper">
                                  <div class="v-messages__message">
                                    {{ errorMessage }}
                                  </div>
                                </div>
                              </div>
                            </v-col>
                          </v-row>
                        </v-form>
                      </v-col>
                      <v-col cols="6">
                        <h3>{{ labelTxt }} Types</h3><br>
                        <ul>
                          <li><strong>Card Range</strong> - Define an {{ labelTxt ? labelTxt.toLowerCase() : label }} card range which should be evaluated for this suspicious activity detection rule.</li>
                          <li><strong>Account ID</strong> - Define an {{ labelTxt ? labelTxt.toLowerCase() : label }} Account ID which should be evaluated for this suspicious activity detection rule.</li>
                          <li v-if="labelTxt !='Exclusion'">
                            <strong>Reward Issuing Participant</strong> - For Redemption transaction types, define an issuing participant whose reward's were included as part of a redemption. For example, if a customer redeemed 0.35 CPG, 0.20 CPG may have been issued by a participant called SodaCo, while the other 0.15 CPG were issued by a separate participant. You may wish to detect fraudulent transactions for customers redeeming SodaCo's rewards.
                            <br><br>
                            <strong>Note:</strong> If the Reward Issuing Participant is added to the inclusions, it will get more precedence than Card Ranges or Account IDs.
                          </li>
                        </ul>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    :id="id + '-save-button'"
                    color="primary"
                    @click="addRange"
                  >
                    Save
                  </v-btn>
                  <v-btn
                    :id="id + '-cancel-button'"
                    color="primary"
                    text
                    @click="dialog = false"
                  >
                    Cancel
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:header.type="{ header }">
          <div>{{ header.text }}</div>
          <v-text-field
            :id="id + '-header-type-filter'"
            v-model="typeFilterValue"
            prepend-icon="mdi-filter-variant"
            type="text"
            clearable
            @click.stop
          />
        </template>
        <template v-slot:header.value="{ header }">
          <div>{{ header.text }}</div>
          <v-text-field
            :id="id + '-header-value-filter'"
            v-model="valueFilterValue"
            prepend-icon="mdi-filter-variant"
            type="text"
            clearable
            @click.stop
          />
        </template>
        <template v-slot:header.description="{ header }">
          <div>{{ header.text }}</div>
          <v-text-field
            :id="id + '-header-description-filter'"
            v-model="descriptionFilterValue"
            prepend-icon="mdi-filter-variant"
            type="text"
            clearable
            @click.stop
          />
        </template>
        <template v-slot:item.value="{ item }">
          {{ getValue(item) }}
        </template>
        <template v-slot:item.action="{ item }">
          <v-icon
            :id="id + '-action-delete'"
            small
            @click="deleteRange(item.from, item.to, item.type)"
          >
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-list>
  </div>
</template>

<script>

  import { api } from '../http/api'
  export default {
    name: 'RangeListInput',

    props: {
      id: String,
      value: Array,
      rangeTypes: Array,
      label: String,
      programId: Number,
      transactionType: String,
      checkTransactionType: {
        type: Function,
        required: true,
      },
    },

    data: () => ({
      dialog: false,
      from: null,
      to: null,
      rangeType: null,
      description: null,
      error: false,
      errorMessage: null,
      rangeFormValid: true,
      inputRules: {
        required: value => !!value || 'Required',
        positiveNumber: value => value > 0 || 'Must be positive number',
        alphanumeric: value => {
          const pattern = /^[a-z0-9]+$/i
          return (!value || pattern.test(value)) || 'Must be alphanumeric'
        },
        maxLength22: value => (!value || value.length <= 22) || 'Max length is 22 digits',
      },
      typeFilterValue: null,
      valueFilterValue: null,
      descriptionFilterValue: null,
      inclusionTypes: [],
      inclusionType: null,
      inclusionTypeCode: null,
      labelTxt: null,
      partAutocomplete: {
        participants: [],
        loading: false,
        search: null,
      },
    }),

    computed: {
      tableHeaders () {
        return [
          {
            text: 'Type',
            value: 'type',
            filter: this.typeFilter,
            class: this.id + '-type',
          },
          {
            text: 'Value',
            value: 'value',
            filter: this.valueFilter,
            class: this.id + '-value',
          },
          {
            text: 'Description',
            value: 'description',
            filter: this.descriptionFilter,
            class: this.id + '-description',
          },
          {
            text: 'Actions',
            value: 'action',
            sortable: false,
            class: this.id + '-action',
          },
        ]
      },
    },

    watch: {
      'partAutocomplete.search' (val) {
        this.loadParticipants(val)
      },
    },

    methods: {
      isSingleValueRange (inclusionType) {
        for (let i = 0; i < this.inclusionTypes.length; i++) {
          if (this.inclusionTypes[i].description.toUpperCase() === inclusionType.toUpperCase()) {
            return this.inclusionTypes[i].code === 'ACCTID' || this.inclusionTypes[i].code === 'RWDISSP'
          }
        }
        return false
      },
      getValue (item) {
        if (item.to) {
          return item.from + ' - ' + item.to
        } else {
          return item.from
        }
      },
      addRange () {
        if (this.validateRanges()) {
          this.value.push({
            from: this.getFrom(this.from, this.participant),
            to: this.isSingleValueRange(this.inclusionType) ? null : this.to,
            type: this.getType(this.inclusionType, this.rangeType).toUpperCase(),
            description: this.description,
            participant: this.participant,
          })

          this.$emit('input', this.value)

          this.clearForm()
          this.checkTransactionType(this.transactionType)
          this.dialog = false
        }
      },
      deleteRange (from, to, type) {
        this.value = this.value.filter((value) => {
          return value.from !== from || value.to !== to || value.type !== type
        })
        this.$emit('input', this.value)
        this.checkTransactionType(this.transactionType)
      },
      validateRanges () {
        if (this.isSingleValueRange(this.inclusionType)) {
          if (!this.validateInputRules()) return false
          if (!this.validateDuplicates()) return false
        } else {
          if (!this.validateInputRules()) return false
          if (!this.validateBothRequired()) return false
          if (!this.validateDuplicates()) return false
          if (!this.validateOrder()) return false
        }

        // all validations have passed
        this.clearErrors()
        return true
      },
      validateInputRules () {
        // validation by inputRules
        return this.$refs.form.validate()
      },
      validateBothRequired () {
        // validation if both ranges are filled
        if (!this.from || !this.to) {
          this.error = true
          this.errorMessage = 'Both values are required!'
          return false
        }
        return true
      },
      validateDuplicates () {
        // validate duplicates
        const enteredFrom = this.getFrom(this.from, this.participant)
        const enteredTo = this.to === null ? undefined : this.to
        const enteredType = this.rangeType === null ? this.inclusionType : this.rangeType

        const duplicates = this.value.filter((value) => {
          const valueTo = value.to === null ? undefined : value.to
          return value.from === enteredFrom && valueTo === enteredTo && value.type.toUpperCase() === enteredType.toUpperCase()
        })

        if (duplicates.length !== 0) {
          this.error = true
          this.errorMessage = 'Range already exists!'
          return false
        }
        return true
      },
      validateOrder () {
        // validate order - from must be lower than to. In case of number compare numerically, in case of string compare lexicographically
        const bothNumbers = !isNaN(this.from) && !isNaN(this.to)

        let correctOrder = true
        if (bothNumbers) {
          if (Number(this.from) > Number(this.to)) {
            correctOrder = false
          }
        } else if (this.from > this.to) {
          correctOrder = false
        }

        if (!correctOrder) {
          this.error = true
          this.errorMessage = 'Range from must be lower than Range to'
          return false
        }
        return true
      },
      clearErrors () {
        this.error = false
        this.errorMessage = null
      },
      clearForm () {
        this.$refs.form.reset()
      },
      typeFilter (value) {
        if (!this.typeFilterValue) {
          return true
        }
        if (!value) {
          return false
        }
        return value.toLowerCase().includes(this.typeFilterValue.toLowerCase())
      },
      valueFilter (a, b, item) {
        if (!this.valueFilterValue) {
          return true
        }

        if (!item.from) {
          return false
        }

        const matchesFrom = item.from.toLowerCase().includes(this.valueFilterValue.toLowerCase())

        if (!item.to) {
          return matchesFrom
        } else {
          const matchesTo = item.to.toLowerCase().includes(this.valueFilterValue.toLowerCase())
          return matchesFrom || matchesTo
        }
      },
      descriptionFilter (value) {
        if (!this.descriptionFilterValue) {
          return true
        }
        if (!value) {
          return false
        }
        return value.toLowerCase().includes(this.descriptionFilterValue.toLowerCase())
      },
      customSort (items, index, isDesc) {
        const fieldName = index[0]
        const desc = isDesc[0]
        items.sort((a, b) => {
          if (fieldName === 'value') {
            if (!desc) {
              return this.getValue(a) < this.getValue(b) ? -1 : 1
            } else {
              return this.getValue(b) < this.getValue(a) ? -1 : 1
            }
          } else {
            if (!desc) {
              return a[fieldName] < b[fieldName] ? -1 : 1
            } else {
              return b[fieldName] < a[fieldName] ? -1 : 1
            }
          }
        })
        return items
      },
      getInclusionTypes () {
        this.labelTxt = this.label.substring(0, (this.label.length - 1))
        if (this.labelTxt === 'Exclusion') {
          api.getExclusionTypes().then(value => {
            this.inclusionTypes = value
          }).catch(err => {
            this.showErrorMessage(err)
          })
        } else if (this.transactionType) {
          api.getInclusionTypes(this.transactionType).then(value => {
            this.inclusionTypes = value
          }).catch(err => {
            this.showErrorMessage(err)
          })
        }
      },
      disableNewBtn () {
        return !this.programId || !this.transactionType
      },
      handleInputFields (inclusionType) {
        this.rangeType = null
        this.from = null
        this.to = null
        this.participant = null
        this.clearErrors()
        for (let i = 0; i < this.inclusionTypes.length; i++) {
          if (this.inclusionTypes[i].description === inclusionType) {
            this.inclusionTypeCode = this.inclusionTypes[i].code
          }
        }
      },
      loadParticipants (term) {
        if (term) {
          this.partAutocomplete.loading = true
          api.getParticipants(this.programId, term)
            .then(value => {
              const ids = new Set(this.partAutocomplete.participants.map(d => d.id))
              this.partAutocomplete.participants = [...this.partAutocomplete.participants, ...value.filter(d => !ids.has(d.id))]
            }).catch(() => {
              const err = { data: { message: 'Could not load participants.' } }
              this.showErrorMessage(err)
            }).finally(() => {
              this.partAutocomplete.loading = false
            })
        }
      },
      getType (inclusionType, rangeType) {
        if (inclusionType) {
          if (rangeType) {
            return rangeType
          } else {
            return inclusionType
          }
        }
      },
      getFrom (fromValue, participant) {
        if (this.inclusionTypeCode === 'RWDISSP') {
          return participant.code
        } else {
          return fromValue
        }
      },
      partAutocompleteItemText: item => item.code + ' — ' + item.description,
      showErrorMessage (err) {
        this.error = true
        if (err.data?.message) {
          this.errorMessage = err.data.message
        } else {
          this.errorMessage = 'Something went wrong.'
        }
      },
    },
  }
</script>

<style scoped>
  .new-btn {
    pointer-events: none;
    background-color: grey !important;
    opacity: 0.6 !important
  }
  ul li {
    text-align: justify;
  }
</style>
